// ── TmContractsList — 계약현황 목록 뷰 (모바일 카드 + 데스크탑 테이블) ─────────
import React from 'react';

// ── 헬퍼 ─────────────────────────────────────────────────────────────────────

/** 날짜 포맷 (YYYY-MM-DD → YYYY.MM.DD) */
function fmtDate(v) { return v ? String(v).replace(/-/g, '.') : '-'; }

/** contract_items 배열 → { names, counts } (비서비스 항목만) */
function parseForDisplay(contractItems) {
  const normal = (contractItems || []).filter((i) => !i.isService);
  if (!normal.length) return { names: '-', counts: '-' };
  return {
    names:  normal.map((i) => i.ticketTypeName || '?').join('\n'),
    counts: normal.map((i) => i.quantity != null ? String(i.quantity) : '-').join('\n'),
  };
}

// ── 서브 컴포넌트 ─────────────────────────────────────────────────────────────

/** 신규/재주문 칩 */
function TypeChip({ isReorder }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: 11, fontWeight: 700, color: '#fff',
      background: isReorder ? '#e67e22' : '#2563eb', whiteSpace: 'nowrap',
    }}>
      {isReorder ? '재주문' : '신규'}
    </span>
  );
}

/** 줄바꿈 문자열을 블록으로 렌더링 */
function MultiLine({ value }) {
  return String(value || '-').split('\n').map((line, i) => (
    <span key={i} style={{ display: 'block' }}>{line}</span>
  ));
}

// 계약 상태 레이블·색상
const STATUS_LABEL = { normal: '정상', cancelled: '취소' };
const STATUS_COLOR = { normal: '#1a7f4b', cancelled: '#c0392b' };

// 테이블 스타일 상수 (dark mode: CSS 변수)
const TH = { padding: '8px 10px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg-subtle)', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', textAlign: 'left' };
const TD = { padding: '8px 10px', fontSize: 13, color: 'var(--text-sub)', borderBottom: '1px solid var(--border)', verticalAlign: 'middle', whiteSpace: 'nowrap' };

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────────
export default function TmContractsList({ rows, loading, isMobile }) {
  if (loading) {
    return <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)', fontSize: 14 }}>불러오는 중...</div>;
  }
  if (!rows.length) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)', fontSize: 14, background: 'var(--bg-subtle)', borderRadius: 8, border: '1px dashed var(--border)' }}>
        해당하는 계약이 없습니다.
      </div>
    );
  }

  // ── 모바일: 카드 ─────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ display: 'grid', gap: 10 }}>
        {rows.map((row) => {
          const { names, counts } = parseForDisplay(row.contract_items);
          const isCancelled = row.contract_status === 'cancelled';
          return (
            <div key={row.id} style={{ border: `1px solid ${isCancelled ? '#fca5a5' : 'var(--border)'}`, borderRadius: 10, padding: '12px 14px', background: isCancelled ? '#fef9f9' : 'var(--bg-card)' }}>
              {/* 1행: 칩 + 계약일 + 상태 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
                <TypeChip isReorder={Boolean(row.is_reorder)} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDate(row.contract_date)}</span>
                <span style={{ marginLeft: 'auto', display: 'inline-block', padding: '2px 9px', borderRadius: 10, fontSize: 11, fontWeight: 600, color: '#fff', background: STATUS_COLOR[row.contract_status] || '#888' }}>
                  {STATUS_LABEL[row.contract_status] || row.contract_status}
                </span>
              </div>
              {/* 2행: 상호명 */}
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 6 }}>
                {row.company_name || '-'}
              </div>
              {/* 3행: 상품·장수 */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 5, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'var(--text-sub)' }}><MultiLine value={names} /></span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}><MultiLine value={counts} /></span>
              </div>
              {/* 4행: 담당자 */}
              {row.sales_person_name && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>담당 · {row.sales_person_name}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── 데스크탑: 테이블 ─────────────────────────────────────────────────────
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-card)' }}>
        <thead>
          <tr>
            <th style={TH}>계약일</th>
            <th style={TH}>구분</th>
            <th style={TH}>상호명</th>
            <th style={TH}>계약 상품</th>
            <th style={TH}>장수</th>
            <th style={TH}>마케팅 담당</th>
            <th style={TH}>상태</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const { names, counts } = parseForDisplay(row.contract_items);
            return (
              <tr key={row.id} style={{ background: row.contract_status === 'cancelled' ? '#fef9f9' : 'var(--bg-card)' }}>
                <td style={TD} title={row.call_date ? `콜일자: ${fmtDate(row.call_date)}` : undefined}>{fmtDate(row.contract_date)}</td>
                <td style={TD}><TypeChip isReorder={Boolean(row.is_reorder)} /></td>
                <td style={{ ...TD, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.company_name || '-'}</td>
                <td style={{ ...TD, maxWidth: 160, overflow: 'hidden' }}><MultiLine value={names} /></td>
                <td style={TD}><MultiLine value={counts} /></td>
                <td style={{ ...TD, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.sales_person_name || '-'}</td>
                <td style={TD}>
                  <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: '#fff', background: STATUS_COLOR[row.contract_status] || '#888' }}>
                    {STATUS_LABEL[row.contract_status] || row.contract_status}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
